/**
 * SocialSignUp
 *
 * Secondary sign-up options: Google OAuth and Enterprise. Defers auth
 * orchestration to parent handlers. Displays error message when provided.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { FcGoogle } from 'react-icons/fc';
import { Building2 } from 'lucide-react';
import { ButtonSpinner } from '../../ui/feedback/loading-spinner.jsx';

export default function SocialSignUp({ onGoogleSignUp, isLoading, error, onInputChange, onEnterpriseSignUp }) {
  // Handler that combines Google sign up with input change
  const handleGoogleClick = () => {
    if (onInputChange) {
      onInputChange();
    }
    onGoogleSignUp();
  };

  // Handler for enterprise sign up
  const handleEnterpriseClick = () => {
    if (onInputChange) {
      onInputChange();
    }
    onEnterpriseSignUp();
  };

  return (
    <div className="mx-auto w-[95%] md:w-[85%] max-w-[600px] mt-4">
      {/* Error message for Google sign-up */}
      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/40 p-4 backdrop-blur-sm border border-red-200 dark:border-red-800 mb-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error</h3>
              <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                {error}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Divider */}
      <div className="relative mt-4">
        <div className="absolute inset-0 flex items-center" aria-hidden="true">
          <div className="w-full border-t border-gray-300 dark:border-gray-700"></div>
        </div>
        <div className="relative flex justify-center text-sm font-medium leading-6">
          <span className="bg-white dark:bg-gray-900 px-6 text-gray-500 dark:text-gray-400">Or sign up with</span>
        </div>
      </div>

      {/* Google Sign Up Button */}
      <div className="mt-4">
        <button
          type="button"
          onClick={handleGoogleClick}
          disabled={isLoading}
          className={`flex w-full items-center justify-center gap-3 rounded-md bg-white dark:bg-gray-800 px-3.5 py-3 text-sm font-semibold text-gray-700 dark:text-white border border-gray-300 dark:border-gray-700 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-400 dark:focus-visible:outline-gray-600 transition-all duration-200 ${isLoading ? 'opacity-95 cursor-not-allowed relative overflow-hidden' : ''}`}
        >
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100/90 dark:bg-gray-700/90 backdrop-blur-sm">
              <div className="flex items-center justify-center">
                <ButtonSpinner color="gray-700 dark:text-white" className="mr-2" />
                <span className="font-medium text-gray-800 dark:text-white">Signing up with Google...</span>
              </div>
            </div>
          )}
          <span className={isLoading ? 'opacity-0' : ''}>
            <span className="flex items-center">
              <FcGoogle className="h-5 w-5 mr-2" />
              Sign up with Google
            </span>
          </span>
        </button>
      </div>

      {/* Enterprise Sign Up Button - Right below Google */}
      <div className="mt-3">
        <button
          type="button"
          onClick={handleEnterpriseClick}
          className="flex w-full items-center justify-center gap-3 rounded-md border border-gray-300 bg-white px-3.5 py-3 text-sm font-semibold text-gray-900 shadow-sm hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600 transition-all duration-200 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
        >
          <Building2 className="h-5 w-5" />
          Sign up with Enterprise Plan
        </button>
      </div>

      {/* Already have an account section */}
      <div className="mt-8 text-center">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Already have an account?
        </p>
        <Link 
          to="/signin" 
          className="mt-2 inline-block font-medium text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300 hover:underline"
          onClick={onInputChange} // Clear errors when navigating away
        >
          Sign in here
        </Link>
      </div>
    </div>
  );
}